/**
 * ccs2rApi.js — Client HTTP pour les endpoints Jury Virtuel IA (Simulateur CCS).
 *
 * Trois endpoints :
 *  - evaluateScenario(cas, diagnostic, objectifs)
 *  - evaluateAnswer(cas, question, theme, userAnswer, reponseModele?)
 *  - generateRelance(cas, question, theme, userAnswer)
 *
 * Toutes les requêtes sont POST JSON, retournent un objet JSON déjà parsé.
 * Gestion d'erreur centralisée + timeout 30s.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BASE = `${API_URL}/api/ccs2r/ai`;

async function postJson(path, body, { signal } = {}) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30_000);
  // Permet une abortion externe (cleanup composant) en plus du timeout
  if (signal) {
    signal.addEventListener('abort', () => controller.abort(), { once: true });
  }
  try {
    const res = await fetch(`${BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    if (!res.ok) {
      let detail = `HTTP ${res.status}`;
      try {
        const j = await res.json();
        detail = j.detail || detail;
      } catch (_e) { /* ignore */ }
      throw new Error(detail);
    }
    return res.json();
  } finally {
    clearTimeout(timeout);
  }
}

export const ccs2rApi = {
  /** Healthcheck (debug). */
  health: async () => {
    const res = await fetch(`${BASE}/health`);
    return res.json();
  },

  /**
   * Évalue le scénario complet (diagnostic + 3 objectifs).
   * @param {Object} payload - {cas, diagnostic, objectifs}
   * @returns {Promise<{score, grade, points_forts, manquants, hors_sujet, recommandations, feedback_jury}>}
   */
  evaluateScenario: (payload, opts) => postJson('/evaluate-scenario', payload, opts),

  /**
   * Évalue une réponse libre à une question jury.
   * @returns {Promise<{score, grade, is_correct, points_valides, points_manquants, erreurs, correction_modele, feedback_jury}>}
   */
  evaluateAnswer: (payload, opts) => postJson('/evaluate-answer', payload, opts),

  /**
   * Génère 1 à 2 relances pédagogiques de jury.
   * @returns {Promise<{relances: Array<{question, intention}>}>}
   */
  generateRelance: (payload, opts) => postJson('/generate-relance', payload, opts),
};

export default ccs2rApi;
